import React, {ReactElement, useCallback, useEffect, useState} from 'react';
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineContent,
  TimelineConnector,
  timelineOppositeContentClasses,
  TimelineOppositeContent
} from '@mui/lab';
import {LoadingButton} from '@mui/lab';
import {GridColDef, useGridApiRef} from '@mui/x-data-grid-premium';
import Dialog, {dialogClasses} from '@mui/material/Dialog';
import {ExpandLessOutlined, ExpandMoreOutlined} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {Controller, useForm} from 'react-hook-form';
import {useSnackbar} from 'notistack';
import {jwtDecode} from 'jwt-decode';
import dayjs from 'dayjs';
import moment from 'moment';
import {DatePicker} from '@mui/x-date-pickers';

import {LoadingScreen} from 'src/minimal-components/loading-screen';
import DropDownPanel from 'src/sections/dropdown/drop-down-panel';
import {useBoolean} from 'src/hooks/use-boolean';
import {APP} from 'src/styles/variables';
import FormProvider from 'src/minimal-components/hook-form';
import AuthUtils from 'src/utils/AuthUtils';
import {fCurrency} from 'src/utils/format-number';
import {
  isValidDate,
  getCurrentMonthStartDate,
  getCurrentMonthEndDate
} from '../../util';

// import {DataGridToolTipTypography} from '@modules/home/main/sub-modules-menu/collapsible-table/CollapsibleTableStyle';

import {StripedDataGrid} from '@components/datagrid/DataGridComponentStyles';
import EmptyContent from '@components/empty-content';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import BasicDatePicker from '@components/form/date-picker/BasicDatePicker';
import CustomPagination from '../table-custom-pagination/TableCustomPagination';
import ActionModalDialog from '@components/dialog/action-dialog/ActionModalDialog';
import FormContainer from '../form-container/FormContainer';
import PaymentEntry from '../payment-entry/PaymentEntry';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import EditIcon from '@mui/icons-material/Edit';
import BatchLedger from '../batch-ledger/BatchLedger';

import {rowStyles} from '@shared/utils/HelperUtils';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';

import {FlexContainer} from './PaymentManualPostStyles';
import {NoRowsOverlay, GridCellRender} from '../../grid-utils';
import {useAppContext} from '@context/AppContext';

const createNewBatchUiFields = {
  LegacyApp: {
    order: 1,
    heading: ['Legacy App', 'Legacy App'],
    placeholder: ['Select Legacy App', ''],
    type: 'select',
    validation: {},
    required: false
  },
  BatchName: {
    order: 1,
    heading: ['Batch Name', 'B Name'],
    placeholder: ['Enter Batch Name', ''],
    type: 'text',
    validation: {
      min_length: 3,
      max_length: 10
    },
    required: true
  },
  DefaultDate: {
    order: 2,
    heading: ['Default Date', 'Default Date'],
    required: true,
    type: 'date',
    validation: {
      min_date: 'current_date | 2022-09-10 | null',
      max_date: '7|30|90|180|365 //days from now'
    }
  },
  ProcessDate: {
    order: 3,
    heading: ['Process Date', 'Process Date'],
    required: true,
    type: 'date',
    validation: {
      min_date: 'current_date | 2022-09-10 | null',
      max_date: '7|30|90|180|365 //days from now'
    }
  },
  AdjudicationDate: {
    order: 4,
    heading: ['Adjudication Date', 'Adjudication Date'],
    required: true,
    type: 'date',
    validation: {
      min_date: 'current_date | 2022-09-10 | null',
      max_date: '7|30|90|180|365 //days from now'
    }
  },
  DefaultTracking: {
    order: 6,
    heading: ['Default Tracking', 'Default Tracking'],
    type: 'text',
    validation: {},
    required: false
  },
  AssignToUser: {
    order: 11,
    heading: ['Secured To User', 'Secured To User'],
    type: 'select',
    validation: {},
    required: false
  },
  TotalPages: {
    order: 12,
    heading: ['Total Pages', 'Total Pages'],
    type: 'number',
    validation: {},
    required: false
  },
  TotalBatchAmount: {
    order: 13,
    heading: ['Total Batch Amount', 'Total Batch Amount'],
    type: 'number',
    validation: {},
    required: false
  },
  IsBbpBatch: {
    order: 14,
    heading: ['BBP Batch', 'BBP Batch'],
    type: 'checkbox',
    validation: {},
    required: false
  }
};

const getValidDate = (date: any) => {
  return date ? dayjs(date).format('YYYY-MM-DD') : null;
};

const PaymentManualPost = (props: any): React.ReactElement => {
  const {t} = useTranslation();
  const theme = useTheme();
  const gridApiRef = useGridApiRef();
  const {enqueueSnackbar} = useSnackbar();
  const batchList = useBoolean();
  const batchSummaryList = useBoolean();
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [createBatchLoading, setCreateBatchLoading] = useState<boolean>(false);
  const [isNewBatchDialog, setIsNewBatchDialog] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [addPaymentDialog, setAddPaymentDialog] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<any>(null);
  const [postingInProgress, setPostingInProgress] = useState(false);
  const [newRemark, setNewRemark] = useState('');
  const [isRemarksDialogOpen, setIsRemarksDialogOpen] = useState(false);
  const [usersInfo, setUsersInfo] = useState<any>([]);
  const [batchRows, setBatchRows] = useState<any[]>([]);
  const [summaryRows, setSummaryRows] = useState<any[]>([]);
  const [isSummaryGridLoading, setIsSummaryGridLoading] = useState(false);
  const [batchListPage, setBatchListPage] = useState(1);
  const [batchListPageSize, setBatchListPageSize] = useState(25);
  const [batchListPageCount, setBatchListPageCount] = useState(1);
  const [batchListTotalRecordsCount, setBatchListTotalRecordsCount] =
    useState(1);
  const [openRemoveBatchDialog, setOpenRemoveBatchDialog] = useState(false);
  const [removeBatchId, setRemoveBatchId] = useState(null);
  const [remarksHistoryLoading, setRemarksHistoryLoading] = useState(false);
  const [remarksHistory, setRemarksHistory] = useState<any>([]);
  const [remarksAddButtonLoading, setRemarksAddButtonLoading] = useState(false);
  const [startDate, setStartDate] = useState<any>(getCurrentMonthStartDate());
  const [endDate, setEndDate] = useState<any>(getCurrentMonthEndDate());
  const [validDateStatus, setValidDateStatus] = useState(false);
  const [payers, setPayers] = useState<any>([]);
  const [selectedBatchIds, setSelectedBatchIds] = React.useState<any>([]);
  const [reviewLoading, setReviewLoading] = React.useState(false);
  const [legacyApps, setLegacyApps] = React.useState<any>([]);
  const [batchLedgerId, setBatchLedgerId] = useState('');
  const [removeBatchLoading, setRemoveBatchLoading] = useState(false);
  const [shouldDisplayEditIcon, setShouldDisplayEditIcon] = useState(false);
  const [batchSortBy, setBatchSortBy] = React.useState<any>([]);
  const [batchFilters, setBatchFilters] = React.useState<any>({
    items: [],
    logicOperator: 'AND'
  });

  const {
    appState: {applicationName}
  } = useAppContext();

  // Get the User Detail
  let userData: any, userEmail: any;
  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
    userEmail = userData?.email || userData?.user?.email;
  }

  const addNewBatchMethods = useForm({});

  const isActionDisabled = (params: any) => {
    if (params.row.PostingStatus === 'Posted') {
      return true;
    }
    if (props?.patientAccountingConfig?.super_user) {
      return false;
    }
    if (
      params.row.AssignToUserEmail === userEmail ||
      params.row.CreatedByUserEmail === userEmail
    ) {
      return false;
    }
    return true;
  };

  const isRemarksDisabled = (params: any) => {
    if (props?.patientAccountingConfig?.super_user) {
      return false;
    }
    if (
      params.row.AssignToUserEmail === userEmail ||
      params.row.CreatedByUserEmail === userEmail
    ) {
      return false;
    }
    return true;
  };

  const isSecuredUserDisabled = (params: any) => {
    if (params.row.PostingStatus === 'Posted') {
      return true;
    }
    if (props?.patientAccountingConfig?.super_user) {
      return false;
    }
    if (params.row.CreatedByUserEmail === userEmail) {
      return false;
    }
    return true;
  };

  const handleMarkCompleteCheckboxChange = (id: number) => {
    setSelectedBatchIds((prev: any) =>
      prev.includes(id)
        ? prev.filter((rowId: any) => rowId !== id)
        : [...prev, id]
    );
  };

  const updateValidDateStatus = (startDate1: any, endDate1: any) => {
    const start = startDate1 ? dayjs(startDate1) : null;
    const end = endDate1 ? dayjs(endDate1) : null;
    if (!start && !end) {
      setValidDateStatus(false);
      return;
    } else if (start && !start.isValid()) {
      setValidDateStatus(false);
      return;
    } else if (end && !end.isValid()) {
      setValidDateStatus(false);
      return;
    }
    if (end && start && end.isBefore(start)) {
      setValidDateStatus(false);
      return;
    }
    setValidDateStatus(true);
  };

  const getBytepadUsers = async () => {
    try {
      const tk = EncryptUtils.encryptedData({
        paymentConfig: props?.paymentConfig,
        applicationName: applicationName
      });
      const usersInfoData = await APIService.getBytepadUsers({tk});
      setUsersInfo([...usersInfoData.data]);
    } catch (e) {
      return;
    }
  };

  const getAllBatches = async (gridLoad = true) => {
    const getBatchPayload = {
      sortBy: batchSortBy,
      filterBy: batchFilters,
      limit: batchListPageSize,
      offset: (batchListPage - 1) * batchListPageSize
    };
    try {
      if (gridLoad) {
        setIsLoading(true);
      }
      const tk = EncryptUtils.encryptedData(getBatchPayload);
      const batchListInfo = await APIService.getManualPostBatches({tk});

      setBatchRows([...batchListInfo.data.batches]);
      setBatchListPageCount(batchListInfo.data.pageCount);
      setBatchListTotalRecordsCount(batchListInfo.data.totalRecordsCount);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const updateBatchData = async (
    batchId: any,
    dataToUpdate: any,
    isPosting = false
  ) => {
    const tk = EncryptUtils.encryptedData({
      batchId,
      batchDetails: dataToUpdate,
      paymentConfig: props?.paymentConfig
    });
    try {
      setIsLoading(true);
      await APIService.updateBatchDetails({tk});
      if (isPosting) {
        enqueueSnackbar(
          'Batch Posted Successfully',
          SnackBarConfig.getSuccess()
        );
      }
      setIsLoading(false);
      getAllBatches();
    } catch (e: any) {
      if (e?.message) {
        enqueueSnackbar(e.message, SnackBarConfig.getError());
      }
      if (e?.code && e?.code === 'DISCREPANCY') {
        getAllBatches();
      }
      setIsLoading(false);
    }
  };

  const getRemarksHistory = async (batchId: any) => {
    try {
      setRemarksHistoryLoading(true);
      const tk = EncryptUtils.encryptedData({
        batchId,
        paymentConfig: props?.paymentConfig
      });
      const remarksData = await APIService.getBatchRemarks({tk});
      setRemarksHistory(remarksData.data);
      setRemarksHistoryLoading(false);
    } catch (e) {
      setRemarksHistoryLoading(false);
    }
  };

  const getBatchSummary = async (start: any, end: any) => {
    const reqPayload = {
      startDate: start
        ? dayjs(start).startOf('day').format('YYYY-MM-DD HH:mm:ss')
        : '',
      endDate: end ? dayjs(end).endOf('day').format('YYYY-MM-DD HH:mm:ss') : '',
      batchType: 'manual_post'
    };
    try {
      setIsSummaryGridLoading(true);
      const tk = EncryptUtils.encryptedData(reqPayload);
      const batchSummaryInfo = await APIService.getBatchSummary({tk});
      setSummaryRows([...batchSummaryInfo.data]);
      setIsSummaryGridLoading(false);
    } catch (e) {
      setIsSummaryGridLoading(false);
    }
  };

  const onRemoveBatch = async () => {
    try {
      setRemoveBatchLoading(true);
      const tk = EncryptUtils.encryptedData({
        batchId: removeBatchId,
        paymentConfig: props?.paymentConfig
      });
      await APIService.removeBatch({tk});
      setRemoveBatchLoading(false);
      getAllBatches();
      setOpenRemoveBatchDialog(false);
      setRemoveBatchId(null);
    } catch (e) {
      setRemoveBatchLoading(false);
    }
  };

  const handleAddRemark = async () => {
    if (newRemark.trim()) {
      setRemarksAddButtonLoading(true);
      try {
        const tk = EncryptUtils.encryptedData({
          batchId: selectedBatchId || '',
          remarks: newRemark.trim(),
          paymentConfig: props?.paymentConfig
        });
        await APIService.addBatchRemarks({tk});
        getAllBatches();
        setNewRemark('');
        setRemarksAddButtonLoading(false);
        setIsRemarksDialogOpen(false);
        setSelectedBatchId(null);
      } catch (e) {
        setRemarksAddButtonLoading(false);
      }
    }
  };

  const onSubmitNewBatch = async (data: any) => {
    const newBatchPayload = {
      newBatchData: {
        BatchType: 'manual_post',
        LegacyApp: data.LegacyApp,
        BatchName: data.BatchName,
        DefaultDate: getValidDate(data.DefaultDate),
        ProcessDate: getValidDate(data.ProcessDate),
        DepositDate: getValidDate(data.ProcessDate),
        AdjudicationDate: getValidDate(data.AdjudicationDate),
        DefaultTracking: data.DefaultTracking,
        AssignToUser: data.AssignToUser,
        IsBbpBatch: data.IsBbpBatch || false,
        TotalBatchAmount: data.TotalBatchAmount,
        TotalPages: data.TotalPages
      },
      paymentConfig: props?.paymentConfig
    };
    if (!newBatchPayload.newBatchData.LegacyApp) {
      enqueueSnackbar('Please select legacy app', SnackBarConfig.getError());
      return;
    }
    if (!newBatchPayload.newBatchData.BatchName) {
      enqueueSnackbar('Please enter batch name', SnackBarConfig.getError());
      return;
    }
    setCreateBatchLoading(true);
    try {
      const tk = EncryptUtils.encryptedData(newBatchPayload);
      await APIService.createNewBatch({tk});
      setIsNewBatchDialog(false);
      setCreateBatchLoading(false);
      addNewBatchMethods.reset();
      getAllBatches();
    } catch (e) {
      setCreateBatchLoading(false);
    }
  };

  const postManualPostBatch = async () => {
    setPostingInProgress(true);
    try {
      const updatedDetails = [
        {name: 'PostingStatus', value: 'Posted'},
        {name: 'PostingStatusReason', value: ''}
      ];
      await updateBatchData(selectedBatchId, updatedDetails, true);
      setSelectedBatchId(null);
      setIsConfirmDialogOpen(false);
      setPostingInProgress(false);
    } catch (e) {
      setPostingInProgress(false);
    }
  };

  const getRCMLegacyApps = async () => {
    setIsPageLoading(true);
    try {
      const tk = EncryptUtils.encryptedData({
        paymentConfig: props?.paymentConfig
      });
      const legacyAppData = await APIService.getRCMLegacyApps({tk});
      setLegacyApps(legacyAppData.data);
      setIsPageLoading(false);
    } catch (e) {
      setIsPageLoading(false);
    }
  };

  const init = async () => {
    getRCMLegacyApps();
    getBytepadUsers();
    getAllBatches();
    getBatchSummary(startDate, endDate);
  };

  const onClosePaymentEntryDialog = () => {
    setAddPaymentDialog(false);
    setSelectedBatchId(null);
    getAllBatches(true);
    getBatchSummary(startDate, endDate);
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    getAllBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchListPage, batchListPageSize, batchFilters, batchSortBy]);

  useEffect(() => {
    updateValidDateStatus(startDate, endDate);
  }, [startDate, endDate]);

  const getActionsStatus = (params: any) => {
    const status = params.row.PostingStatus;
    const variance = params.row.TotalVariance;
    if (status === 'Not Posted') {
      return {
        paymentDisabled: false,
        postDisabled: true,
        viewDisabled: true,
        removeDisabled: false,
        markCompleteDisabled: true,
        bbpBatchDisabled: false
      };
    } else if (status === 'In Progress') {
      return {
        paymentDisabled: false,
        postDisabled: variance !== 0,
        viewDisabled: true,
        removeDisabled: false,
        markCompleteDisabled: true,
        bbpBatchDisabled: false
      };
    } else if (status === 'Balanced') {
      return {
        paymentDisabled: false,
        postDisabled: variance !== 0,
        viewDisabled: variance !== 0,
        removeDisabled: false,
        markCompleteDisabled: true,
        bbpBatchDisabled: false
      };
    } else if (status === 'Posted') {
      return {
        paymentDisabled: true,
        postDisabled: true,
        viewDisabled: true,
        removeDisabled: true,
        markCompleteDisabled: false,
        bbpBatchDisabled: true
      };
    }
    return {
      paymentDisabled: false,
      postDisabled: false,
      viewDisabled: false,
      removeDisabled: false,
      markCompleteDisabled: false,
      bbpBatchDisabled: false
    };
  };

  const getStatusFontColor = (status: string) => {
    if (['Posted', 'Balanced'].includes(status)) return 'success.main';
    else if (status === 'In Progress') return '#FFBF00';
    else if (status === 'Unprocessed') return 'error.main';
    else return 'black';
  };

  // -----------------------------------------------------

  // Actions
  const handleAddPaymentClick = (row: any) => {
    setSelectedBatchId(row.id);
    setAddPaymentDialog(true);
  };

  const handlePostClick = (row: any) => {
    setSelectedBatchId(row.id);
    setIsConfirmDialogOpen(true);
  };

  const handleViewClick = (row: any) => {
    setBatchLedgerId(row.id);
  };

  const handleRemoveClick = (row: any) => {
    setRemoveBatchId(row.id);
    setOpenRemoveBatchDialog(true);
  };

  const handleBBPBatchStatusChange = (id: string, checked: boolean) => {
    const dataToUpdate = [{name: 'IsBbpBatch', value: checked}];
    updateBatchData(id, dataToUpdate);
  };

  const handlePostConfirmation = async (confirmed: boolean) => {
    if (confirmed && selectedBatchId) {
      setPostingInProgress(true);
      postManualPostBatch();
    } else {
      setSelectedBatchId(null);
      setIsConfirmDialogOpen(false);
    }
  };

  const handleCloseDialog = () => {
    setIsRemarksDialogOpen(false);
    setSelectedBatchId(null);
    setRemarksHistory([]);
  };

  const onChangeStartDate = (e: any) => {
    setStartDate(e);
  };

  const onChangeEndDate = (e: any) => {
    setEndDate(e);
  };

  const handleCreateBatch = async () => {
    addNewBatchMethods.reset();
    setIsNewBatchDialog(true);
    addNewBatchMethods.setValue('DefaultDate', new Date().toISOString());
  };

  const handleAddRemarkClick = (batchId: string) => {
    setSelectedBatchId(batchId);
    setIsRemarksDialogOpen(true);
    getRemarksHistory(batchId);
  };

  const handleClose = useCallback(() => {
    setIsNewBatchDialog(false);
  }, [isNewBatchDialog]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      event.target.value = '';
    }
  };

  const onRefreshBatchList = async () => {
    await getAllBatches();
  };

  const onReviewBatchList = async () => {
    if (!selectedBatchIds.length) return;
    setReviewLoading(true);
    const tk = EncryptUtils.encryptedData({
      batchIds: selectedBatchIds,
      paymentConfig: props?.paymentConfig
    });
    try {
      await APIService.markBatchesReviewed({tk});
      setReviewLoading(false);
      setSelectedBatchIds([]);
      onRefreshBatchList();
    } catch (e) {
      setReviewLoading(false);
    }
  };

  const processRowUpdate = async (
    newRow: any,
    oldRow: any,
    displayType: string
  ) => {
    if (newRow.BatchName && newRow.BatchName !== oldRow.BatchName) {
      const updatePayload = [{name: 'BatchName', value: newRow.BatchName}];
      updateBatchData(newRow.id, updatePayload);
    } else if (
      newRow.TotalBatchAmount &&
      newRow.TotalBatchAmount !== oldRow.TotalBatchAmount
    ) {
      const updatePayload = [
        {name: 'TotalBatchAmount', value: Number(newRow.TotalBatchAmount)}
      ];
      updateBatchData(newRow.id, updatePayload);
    } else if (
      newRow.AssignToUserName &&
      newRow.AssignToUserName !== oldRow.AssignToUserName
    ) {
      const updateDetails = [
        {name: 'AssignToUser', value: newRow.AssignToUserName}
      ];
      const assignToUserObj = usersInfo.find(
        (u: any) => u.id === newRow.AssignToUserName
      );
      newRow.AssignToUserName = assignToUserObj ? assignToUserObj.fullName : '';
      newRow.AssignToUser = newRow.AssignToUserName;
      updateBatchData(newRow.id, updateDetails);
    }
    return newRow;
  };

  const onClearFilters = async () => {
    setStartDate(null);
    setEndDate(null);
    getBatchSummary(null, null);
  };

  const onApplyButtonClick = async () => {
    if (!validDateStatus) return;
    getBatchSummary(startDate, endDate);
  };

  const onCloseRemoveBatchDialog = () => {
    setOpenRemoveBatchDialog(false);
    setRemoveBatchId(null);
  };

  const getRemoveBatchName = () => {
    const batchObj = batchRows.find(br => br.id === removeBatchId);
    return batchObj?.BatchName || '';
  };

  const handleTextFieldChange = (
    e: any,
    inputName: string,
    formType: string
  ) => {
    if (!e.target.value) return;
  };

  const getUsersOptions = () => {
    return usersInfo.map((u: any) => ({
      label: u.fullName,
      value: u.id
    }));
  };

  const getLegacyAppOptions = () => {
    return legacyApps.map((l: any) => ({
      label: l.LegacyApp,
      value: l.LegacyApp
    }));
  };

  const getSelectFieldOptions = (inputName: string, formType: string) => {
    switch (inputName) {
      case 'AssignToUser':
        return getUsersOptions();
      case 'LegacyApp':
        return getLegacyAppOptions();
      default:
        return [];
    }
  };

  const handleSelectFieldChange = (e: any, inputName: string) => {};

  const handleButtonFieldClick = (
    e: any,
    inputName: string,
    formType: string
  ) => {};

  const getBtnDisabledStatus = (inputName: string, formType: string) => {
    return false;
  };

  const getBtnLoadingStatus = (inputName: string, formType: string) => {
    return false;
  };

  const getButtonColorSx = (inputName: string, formType: string) => {
    const colorSx = {
      backgroundColor: ''
    };
    const actions = new Map<string, string>([
      ['delete', theme.palette.primary.lighter],
      ['new', theme.palette.primary.dark],
      ['cancel', theme.palette.primary.light],
      ['save', 'success.main']
    ]);

    if (formType === 'payment_entry') {
      const colorToMap = actions.get(inputName) || '';
      colorSx.backgroundColor = colorToMap;
    }
    return colorSx;
  };

  // UI Fields
  const renderSearchButton = () => {
    return (
      <LoadingButton
        type="submit"
        variant="contained"
        loading={createBatchLoading}
        sx={{
          backgroundColor: theme.palette.primary.dark
        }}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          addNewBatchMethods.handleSubmit(onSubmitNewBatch)();
        }}>
        {t('PRIVATE.COMMON.SAVE', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderCancelButton = () => {
    return (
      <LoadingButton
        type="button"
        variant="outlined"
        onClick={() => {
          addNewBatchMethods.reset();
          handleClose();
        }}
        disabled={false}
        sx={{
          mr: 2,
          backgroundColor: theme.palette.primary.light
        }}>
        {t('PRIVATE.ROLES.CANCEL', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderYesButton = () => {
    return (
      <LoadingButton
        type="button"
        variant="outlined"
        onClick={() => handlePostConfirmation(true)}
        sx={{
          mr: 2,
          backgroundColor: theme.palette.primary.dark
        }}>
        {t('PRIVATE.PATIIENT_ACCOUNTING.CONFIRM_POST.YES_LABEL_TEXT', {
          ns: 'private'
        })}
      </LoadingButton>
    );
  };

  const renderNoButton = () => {
    return (
      <LoadingButton
        type="button"
        variant="outlined"
        onClick={() => handlePostConfirmation(false)}
        sx={{
          backgroundColor: theme.palette.primary.light
        }}>
        {t('PRIVATE.PATIIENT_ACCOUNTING.CONFIRM_POST.NO_LABEL_TEXT', {
          ns: 'private'
        })}
      </LoadingButton>
    );
  };

  const renderFormContainer = (): ReactElement => {
    return (
      <FormProvider
        methods={addNewBatchMethods}
        onSubmit={addNewBatchMethods.handleSubmit(onSubmitNewBatch)}>
        <Stack spacing={2}>
          <DialogTitle
            sx={{
              textAlign: 'left',
              borderBottom: `solid 1px ${theme.palette.divider}`
            }}>
            New Batch
          </DialogTitle>
          <DialogContent>
            <FormContainer
              uiDisabled={false}
              formType="AddNewBatch"
              uiFields={createNewBatchUiFields}
              formMethods={addNewBatchMethods}
              gridItemsPerRow={3}
              handleSelectFieldChange={handleSelectFieldChange}
              getSelectFieldOptions={getSelectFieldOptions}
              handleFileChange={handleFileChange}
              handleTextFieldChange={handleTextFieldChange}
              getBtnLoadingStatus={getBtnLoadingStatus}
              getBtnDisabledStatus={getBtnDisabledStatus}
              handleButtonFieldClick={handleButtonFieldClick}
              getButtonColorSx={getButtonColorSx}
            />
          </DialogContent>
          <DialogActions>
            <Stack alignItems="flex-end">
              <Grid item xs={12} display="flex" sm={6} md={12}>
                {renderCancelButton()}
                {renderSearchButton()}
              </Grid>
            </Stack>
          </DialogActions>
        </Stack>
      </FormProvider>
    );
  };

  const renderRemarksTimeline = (): ReactElement => {
    return (
      <>
        <DialogTitle
          sx={{
            textAlign: 'left',
            borderBottom: `solid 1px ${theme.palette.divider}`
          }}>
          Remarks History
        </DialogTitle>
        <DialogContent>
          <Box p={1}>
            <TextField
              label="Add Remark"
              fullWidth
              multiline
              rows={4}
              value={newRemark}
              onChange={e => setNewRemark(e.target.value)}
              sx={{marginBottom: '16px'}}
            />
            <Stack
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              spacing={2}>
              <LoadingButton
                type="button"
                variant="outlined"
                onClick={handleCloseDialog}
                sx={{
                  backgroundColor: theme.palette.primary.light
                }}>
                Close
              </LoadingButton>
              <LoadingButton
                loading={remarksAddButtonLoading}
                type="button"
                variant="contained"
                onClick={handleAddRemark}
                fullWidth>
                Add Remark
              </LoadingButton>
            </Stack>
            <Box
              mt={2}
              sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                padding: '10px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
              {remarksHistory.length === 0 ? (
                <Box m={2}>
                  {remarksHistoryLoading ? (
                    <LoadingScreen />
                  ) : (
                    <Typography
                      textAlign={'center'}
                      variant="body2"
                      color="textSecondary">
                      No remarks yet.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Timeline
                  position="right"
                  sx={{
                    [`& .${timelineOppositeContentClasses.root}`]: {
                      flex: 0.2
                    }
                  }}>
                  {remarksHistory.map((remark: any, index: number) => (
                    <TimelineItem key={index}>
                      <TimelineOppositeContent color="textSecondary">
                        Added by{' '}
                        <span
                          style={{
                            fontWeight: 'bold'
                          }}>
                          {remark.CreatedByName}
                        </span>{' '}
                        on{' '}
                        <span
                          style={{
                            fontStyle: 'italic',
                            width: '100%',
                            fontWeight: 'bold'
                          }}>
                          {moment(remark.BytepadCreatedDate).format(
                            'MM/DD/YYYY hh:mm A'
                          )}
                        </span>
                      </TimelineOppositeContent>

                      <TimelineSeparator>
                        <TimelineDot
                          variant="filled"
                          color="primary"
                          sx={{
                            backgroundColor: 'primary.main',
                            boxShadow: '0 0 10px rgba(6, 214, 160, 0.6)'
                          }}
                        />
                        <TimelineConnector sx={{backgroundColor: '#06D6A0'}} />
                      </TimelineSeparator>

                      <TimelineContent
                        sx={{
                          paddingLeft: '16px',
                          fontSize: '1rem',
                          color: '#333',
                          fontWeight: '400'
                        }}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{fontStyle: 'italic'}}>
                          {`"${remark.Remarks}"`}
                        </Typography>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              )}
            </Box>
          </Box>
        </DialogContent>
      </>
    );
  };

  const getUsersOptionsForSingleSelect = () => {
    return usersInfo
      ? usersInfo.map((u: any) => ({
          label: u.fullName,
          value: u.id
        }))
      : [];
  };

  const isRowSelectable = (params: any, gridDisplayType: string) => {
    if (gridDisplayType === 'batch_view') {
      const {markCompleteDisabled} = getActionsStatus(params);
      return !markCompleteDisabled;
    }
    return false;
  };

  const isCellEditable = (params: any, gridDisplayType: string) => {
    if (['BatchName'].includes(params.field)) {
      return params.row.PostingStatus === 'Not Posted';
    } else if (params.field === 'AssignToUserName') {
      return isSecuredUserDisabled(params) ? false : true;
    } else if (['DepositDate', 'TotalBatchAmount'].includes(params.field)) {
      return isActionDisabled(params) ? false : true;
    }
    return true;
  };

  const onRowSelectionModelChange = (e: any, gridDisplayType: string) => {
    if (gridDisplayType === 'batch_view') {
      setSelectedBatchIds(e);
    }
  };

  const getDataGridPaginationProps = (gridDisplayType: string) => {
    const paginationProps = {
      pageNo: 0,
      pageSize: 0,
      rowsPerPageOptions: [25, 50, 100],
      pageCount: 0,
      totalRecordsCount: 0,
      totalRows: 0
    };
    if (gridDisplayType === 'batch_view') {
      paginationProps.pageNo = batchListPage;
      paginationProps.pageSize = batchListPageSize;
      paginationProps.pageCount = batchListPageCount;
      paginationProps.totalRecordsCount = batchListTotalRecordsCount;
      paginationProps.totalRows = batchRows.length;
    }
    return paginationProps;
  };

  const setGridPaginationPage = (e: any, gridDisplayType: string) => {
    if (gridDisplayType === 'batch_view') {
      setBatchListPage(e);
    }
  };

  const setGridPaginationPageSize = (e: any, gridDisplayType: string) => {
    if (gridDisplayType === 'batch_view') {
      setBatchListPageSize(e);
    }
  };

  const batchListColumns: GridColDef[] = [
    {
      headerName: 'Mark Complete',
      field: 'selection',
      width: 70,
      sortable: false,
      filterable: false,
      type: 'boolean',
      renderCell: (params: any) => (
        <Checkbox
          disabled={!isRowSelectable(params, 'batch_view')}
          color="success"
          checked={selectedBatchIds.includes(params.row.id)}
          onChange={() => handleMarkCompleteCheckboxChange(params.row.id)}
        />
      )
    },
    {
      headerName: 'Batch ID',
      field: 'batchId',
      type: 'number',
      sortable: true,
      filterable: true,
      width: 70,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: any) =>
        params.row.id !== 'summary_record' ? (
          <GridCellRender value={params.value} />
        ) : (
          <GridCellRender
            value={params.value}
            typographySx={{fontWeight: 'bold'}}
          />
        )
    },
    {
      headerName: 'Created On',
      field: 'BytepadCreatedDate',
      sortable: true,
      filterable: true,
      type: 'date',
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : ''
          }
        />
      )
    },
    {
      headerName: 'Created By',
      field: 'CreatedBy',
      sortable: true,
      filterable: true,
      type: 'string',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Batch Name',
      field: 'BatchName',
      sortable: true,
      filterable: true,
      editable: true,
      type: 'string',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Deposit Date',
      field: 'DepositDate',
      sortable: true,
      filterable: true,
      type: 'date',
      width: 180,
      renderCell: (params: any) => {
        const handleDateChange = (newDate: Date | null) => {
          const dataToUpdate = [
            {
              name: 'DepositDate',
              value: dayjs(newDate).format('YYYY-MM-DD')
            }
          ];
          if (isValidDate(getValidDate(newDate))) {
            updateBatchData(params.row.id, dataToUpdate);
          }
        };

        return (
          <Controller
            name={`depositDate-${params.row.id}`}
            control={addNewBatchMethods.control}
            defaultValue={params.value}
            render={({field}) => (
              <BasicDatePicker
                disabled={isActionDisabled(params)}
                yearFormatView={false}
                label={''}
                error={false}
                field={field}
                handleChange={handleDateChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderColor: 'transparent'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiInputBase-input': {
                    padding: '6px 14px'
                  }
                }}
              />
            )}
          />
        );
      },
      valueGetter: (params: any) => {
        return params.value ? moment.utc(params.value).toDate() : null;
      }
    },
    {
      headerName: 'Total Pages',
      field: 'TotalPages',
      sortable: true,
      filterable: true,
      type: 'string',
      renderCell: (params: any) =>
        params.row.id !== 'summary_record' ? (
          <GridCellRender value={params.value} />
        ) : (
          <GridCellRender
            value={params.row.sumValue}
            typographySx={{fontWeight: 'bold'}}
          />
        )
    },
    {
      headerName: 'Total Batch Amount',
      field: 'TotalBatchAmount',
      sortable: true,
      filterable: true,
      type: 'number',
      editable: true,
      renderCell: (params: any) =>
        params.row.id !== 'summary_record' ? (
          <GridCellRender value={fCurrency(params.value)} />
        ) : (
          <GridCellRender
            value={fCurrency(params.row.sumValue)}
            typographySx={{fontWeight: 'bold'}}
          />
        )
    },
    {
      headerName: 'Total Batch Posted Amount',
      field: 'TotalBatchAmountPosted',
      sortable: true,
      filterable: true,
      type: 'number',
      renderCell: (params: any) =>
        params.row.id !== 'summary_record' ? (
          <GridCellRender value={fCurrency(params.value)} />
        ) : (
          <GridCellRender
            value={fCurrency(params.row.sumValue)}
            typographySx={{fontWeight: 'bold'}}
          />
        )
    },
    {
      headerName: 'Total Variance',
      field: 'TotalVariance',
      sortable: true,
      filterable: true,
      type: 'number',
      renderCell: (params: any) =>
        params.row.id !== 'summary_record' ? (
          <GridCellRender value={fCurrency(params.value)} />
        ) : (
          <GridCellRender
            value={fCurrency(params.row.sumValue)}
            typographySx={{fontWeight: 'bold'}}
          />
        )
    },
    {
      headerName: 'Posted Date',
      field: 'PostedDate',
      sortable: true,
      filterable: true,
      type: 'date',
      valueGetter: (params: any) =>
        params.value ? moment.utc(params.value).toDate() : null,
      renderCell: (params: any) => (
        <GridCellRender
          value={
            params.value ? moment.utc(params.value).format('MM/DD/YYYY') : ''
          }
        />
      )
    },
    {
      headerName: 'Secure To User',
      field: 'AssignToUserName',
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      editable: true,
      width: 150,
      valueOptions: getUsersOptionsForSingleSelect(),
      renderCell: (params: any) => {
        return shouldDisplayEditIcon ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              width: '100%'
            }}>
            <GridCellRender value={params.value} />
            <IconButton
              sx={{color: 'success.main'}}
              disabled={params.row.PostingStatus !== 'Not Posted'}
              size="small"
              onClick={() =>
                params.api.startCellEditMode({
                  id: params.id,
                  field: params.field
                })
              }>
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <GridCellRender value={params.value} />
        );
      }
    },
    {
      headerName: 'Correspondence',
      field: 'Correspondence',
      sortable: true,
      filterable: true,
      type: 'string',
      renderCell: (params: any) => <GridCellRender value={params.value} />
    },
    {
      headerName: 'Status',
      field: 'PostingStatus',
      sortable: true,
      filterable: true,
      type: 'string',
      renderCell: (params: any) => (
        <GridCellRender
          value={params.value}
          typographySx={{
            color: getStatusFontColor(params.row.PostingStatus),
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      headerName: 'Remarks',
      field: 'remarks',
      sortable: true,
      filterable: false,
      type: 'string',
      flex: 0,
      width: 120,
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%'
            }}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 'calc(100% - 50px)'
              }}>
              {params.value}
            </Typography>
            <LoadingButton
              variant="outlined"
              disabled={isRemarksDisabled(params)}
              onClick={() => handleAddRemarkClick(params.row.id)}
              sx={{
                minWidth: 'unset',
                padding: '0px 8px',
                fontSize: '12px',
                color: theme.palette.primary.main
              }}>
              View
            </LoadingButton>
          </Box>
        );
      }
    },
    {
      headerName: 'BBP Batch Status',
      field: 'IsBbpBatch',
      sortable: true,
      filterable: true,
      type: 'boolean',
      renderCell: (params: any) => {
        const {bbpBatchDisabled} = getActionsStatus(params);

        return (
          <Checkbox
            disabled={bbpBatchDisabled || isActionDisabled(params)}
            checked={params.value}
            onChange={(_event: React.SyntheticEvent, checked: boolean) => {
              handleBBPBatchStatusChange(params.row.id, checked);
            }}
            color="success"
          />
        );
      }
    },
    {
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      filterable: false,
      type: 'string',
      flex: 0,
      width: 400,
      renderCell: (params: any) => {
        const {paymentDisabled, postDisabled, removeDisabled, viewDisabled} =
          getActionsStatus(params);
        const actionStatus = isActionDisabled(params);
        return (
          <Box sx={{display: 'flex', gap: 1, width: '100%'}}>
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={postDisabled || actionStatus}
              size="small"
              onClick={() => handlePostClick(params.row)}
              sx={{
                backgroundColor: theme.palette.primary.dark
              }}>
              Post
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={viewDisabled || actionStatus}
              size="small"
              onClick={() => handleViewClick(params.row)}
              sx={{
                backgroundColor: theme.palette.primary.main,
                minWidth: 'unset'
              }}>
              Transaction List
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              disabled={paymentDisabled || actionStatus}
              size="small"
              onClick={() => handleAddPaymentClick(params.row)}
              sx={{
                backgroundColor: theme.palette.primary.darker
              }}>
              Payment Entry
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={removeDisabled || actionStatus}
              size="small"
              onClick={() => handleRemoveClick(params.row)}
              sx={{
                backgroundColor: theme.palette.primary.lighter
              }}>
              {t('PRIVATE.PATIIENT_ACCOUNTING.PAYMENT.REMOVE', {
                ns: 'private'
              })}
            </LoadingButton>
          </Box>
        );
      }
    }
  ];

  const summaryColumns: GridColDef[] = [
    {
      field: 'PostingStatus',
      headerName: 'Status',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params: any) => {
        return params.row.id === 'summary_record' ? (
          <Typography sx={{fontWeight: 'bold'}}>{params.value}</Typography>
        ) : (
          params.value
        );
      }
    },
    {
      field: 'BatchCount',
      headerName: 'Batch Count',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params: any) => {
        return params.row.id === 'summary_record' ? (
          <Typography sx={{fontWeight: 'bold'}}>{params.value}</Typography>
        ) : (
          params.value
        );
      }
    },
    {
      field: 'Payment',
      headerName: 'Amount',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params: any) => {
        return params.row.id === 'summary_record' ? (
          <Typography sx={{fontWeight: 'bold'}}>
            {fCurrency(params.value)}
          </Typography>
        ) : (
          fCurrency(params.value)
        );
      }
    }
  ];

  const onSortModelChange = (sortModel: any, type: string) => {
    if (type !== 'batch_view') return;
    if (sortModel) {
      setBatchSortBy([...sortModel]);
    }
  };

  const onFilterModelChange = (filterModel: any, type: string) => {
    if (type !== 'batch_view') return;
    if (filterModel) {
      const filterModelItems = filterModel.items.map((item: any) => {
        return {
          ...item,
          column_type: batchListColumns.find(f => f.field === item.field)?.type
        };
      });

      setBatchFilters({
        items: filterModelItems,
        logicOperator: filterModel.logicOperator
      });
    }
  };

  const getDataGridProps = (gridDisplayType: string) => {
    let gridColumns, gridRows, gridLoading;
    if (gridDisplayType === 'batch_view') {
      gridColumns = batchListColumns;
      gridLoading = isLoading;
      gridRows = batchRows;
    } else if (gridDisplayType === 'summary_view') {
      gridColumns = summaryColumns;
      gridLoading = isSummaryGridLoading;
      gridRows = summaryRows;
    }
    const gridPropsObj = {
      gridPaginationStatus: ['batch_view'].includes(gridDisplayType),
      gridHideFooterStatus: !['batch_view'].includes(gridDisplayType),
      gridColumns: gridColumns || [],
      gridRows: gridRows || [],
      gridLoading: gridLoading || false,
      gridCheckboxSelection: false,
      horizontalScroll: [
        'batch_view',
        'payment_entry_view',
        'cob_window'
      ].includes(gridDisplayType)
    };
    return gridPropsObj;
  };

  const renderDataGridComponent = (gridDisplayType: string) => {
    const {
      gridPaginationStatus,
      gridHideFooterStatus,
      gridColumns,
      gridRows,
      gridLoading,
      gridCheckboxSelection,
      horizontalScroll
    } = getDataGridProps(gridDisplayType);

    const {
      pageNo,
      pageSize,
      rowsPerPageOptions,
      pageCount,
      totalRecordsCount,
      totalRows
    } = getDataGridPaginationProps(gridDisplayType);
    return (
      <Box
        px={3}
        sx={{
          marginTop: '1rem',
          marginBottom: '1rem',
          maxHeight: 'auto'
        }}>
        <StripedDataGrid
          showColumnVerticalBorder
          showCellVerticalBorder
          autoHeight
          disableRowGrouping
          disableRowSelectionOnClick
          disableColumnSelector
          disableAggregation
          checkboxSelection={gridCheckboxSelection}
          apiRef={gridApiRef}
          loading={gridLoading}
          columns={gridColumns}
          rows={gridRows}
          rowThreshold={0}
          rowHeight={37}
          columnHeaderHeight={42}
          hideFooter={gridHideFooterStatus}
          pagination={gridPaginationStatus}
          paginationMode="server"
          sortingMode="server"
          sortingOrder={['desc', 'asc']}
          filterMode="server"
          onSortModelChange={(model: any) =>
            onSortModelChange(model, gridDisplayType)
          }
          onFilterModelChange={(model: any) =>
            onFilterModelChange(model, gridDisplayType)
          }
          processRowUpdate={(newRow: any, oldRow: any) =>
            processRowUpdate(newRow, oldRow, gridDisplayType)
          }
          // getCellClassName={getCellClassName}
          isCellEditable={(e: any) => isCellEditable(e, gridDisplayType)}
          isRowSelectable={(e: any) => isRowSelectable(e, gridDisplayType)}
          onRowSelectionModelChange={(e: any) =>
            onRowSelectionModelChange(e, gridDisplayType)
          }
          getRowId={(row: any) => row.id || row.ID || row.BatchId}
          onProcessRowUpdateError={(error: any) => {
            return true;
          }}
          slotProps={{
            filterPanel: {
              sx: {'& .MuiDataGrid-filterFormValueInput': {width: 'auto'}}
            }
          }}
          getRowClassName={(params: any) => {
            return rowStyles(params);
          }}
          sx={{
            backgroundColor: `${APP.PALETTE.COMMON.WHITE}`,
            '& .MuiTablePagination-displayedRows': {
              display: 'none'
            },
            '& .MuiPagination-ul': {
              flexWrap: 'nowrap'
            },
            '& .MuiDataGrid-row': {
              border: '1px solid lightgray',
              borderTopWidth: '0px',
              width: 'calc(100% - 2px)'
            },
            '& .MuiDataGrid-columnHeaders': {
              border: '1px solid lightgray',
              backgroundColor: `${theme.palette.grey[300]}`,
              borderBottomColor: `${theme.palette.primary.lighter}`,
              borderBottomWidth: '1px'
            },
            '& .MuiDataGrid-columnHeader--moving': {
              backgroundColor: `${APP.PALETTE.DATA_GRID.HEADER}`
            },
            '& .AbnormalFlagText': {
              color: `${APP.PALETTE.ABNORMAL_FLAG.TEXT}`,
              fontWeight: 'bold !important'
            },
            '& .css-87hgir-MuiTypography-root': {
              fontWeight: 'unset'
            },
            '& .css-11d09j8-MuiButtonBase-root-MuiCheckbox-root.Mui-checked': {
              color: props?.checkboxColor || `${theme.palette.primary.lighter}`
            },
            '& .MuiDataGrid-virtualScroller': {
              minHeight: '70px',
              scrollBehavior: 'smooth',
              overflow: horizontalScroll ? 'auto' : 'visible !important'
            },
            '& .MuiDataGrid-footerContainer': {
              marginBottom: '1rem'
            },
            '& .MuiDataGrid-detailPanel': {
              border: '1px solid lightgray'
            },
            '& .MuiDataGrid-pinnedRowsRenderZone': {
              fontWeight: 'bolder'
            },
            '.autopost-grid-editable-cell': {
              backgroundColor: '#e0f7fa',
              color: '#004d40'
            },
            px: 3,
            pr: 0,
            pl: 0
          }}
          slots={{
            detailPanelExpandIcon: ExpandMoreOutlined,
            detailPanelCollapseIcon: ExpandLessOutlined,
            pagination: () => (
              <CustomPagination
                page={pageNo}
                pageSize={pageSize}
                rowsPerPageOptions={rowsPerPageOptions}
                pageCount={pageCount}
                totalRecordsCount={totalRecordsCount}
                totalRows={totalRows}
                setPage={(e: any) => setGridPaginationPage(e, gridDisplayType)}
                setPageSizes={(e: any) =>
                  setGridPaginationPageSize(e, gridDisplayType)
                }
              />
            ),
            noRowsOverlay: () => (
              <EmptyContent
                sx={{
                  py: 10
                }}
                title="No Data"
              />
            ),
            noResultsOverlay: () => (
              <EmptyContent
                sx={{
                  py: 10
                }}
                title="No results found"
              />
            )
          }}
        />
      </Box>
    );
  };

  if (batchLedgerId) {
    const batchDetailInfo = batchRows?.find(
      (tc: any) => tc.id === batchLedgerId
    );
    return (
      <BatchLedger
        batchId={batchLedgerId}
        onClose={() => setBatchLedgerId('')}
        batchInfo={batchDetailInfo}
        paymentPageLabel={props?.paymentPageLabel}
        paymentConfig={props?.paymentConfig}
      />
    );
  }

  if (addPaymentDialog) {
    const currentBatchInfo = batchRows?.find(
      (tc: any) => tc.id === selectedBatchId
    );
    const postingLevel = currentBatchInfo?.PostingLevel;
    return (
      <PaymentEntry
        payers={payers}
        paymentConfig={props?.paymentConfig}
        onClosePaymentEntry={onClosePaymentEntryDialog}
        batchId={selectedBatchId}
        batchInfo={currentBatchInfo}
        paymentPageLabel={props?.paymentPageLabel}
        postingLevel={postingLevel}
      />
    );
  }

  return isPageLoading ? (
    <CircularProgressDiv>
      <LoadingScreen />
    </CircularProgressDiv>
  ) : (
    <Stack mt={2}>
      {/* Create New Batch */}
      <Box px={3}>
        <LoadingButton
          type="submit"
          variant="contained"
          disabled={false}
          onClick={handleCreateBatch}
          sx={{
            backgroundColor: theme.palette.primary.dark,
            width: 'auto',
            maxWidth: '180px'
          }}>
          {t('PRIVATE.PATIIENT_ACCOUNTING.PAYMENT.CREATE_NEW_BATCH', {
            ns: 'private'
          })}
        </LoadingButton>
      </Box>
      <Box px={3} mt={1}>
        <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
          <Box
            rowGap={3}
            columnGap={2}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(3, 1fr)'
            }}>
            <DatePicker
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0000003b',
                  borderWidth: '1.8px'
                }
              }}
              label="Deposit Date From"
              value={startDate}
              onChange={onChangeStartDate}
            />
            <DatePicker
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0000003b',
                  borderWidth: '1.8px'
                }
              }}
              label="Deposit Date To"
              value={endDate}
              onChange={onChangeEndDate}
            />
          </Box>
          <Box
            sx={{
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}>
            <LoadingButton
              type="reset"
              onClick={onClearFilters}
              disabled={startDate || endDate ? false : true}
              variant="outlined"
              sx={{
                marginLeft: '24px',
                backgroundColor: theme.palette.primary.light
              }}>
              Reset
            </LoadingButton>
            <LoadingButton
              type="submit"
              onClick={onApplyButtonClick}
              disabled={!validDateStatus}
              variant="contained"
              sx={{
                marginLeft: '12px',
                backgroundColor: theme.palette.primary.dark
              }}>
              Search
            </LoadingButton>
          </Box>
          <DropDownPanel
            mt={2}
            title={'Batch Summary'}
            collapse={batchSummaryList.value}
            onCollapse={batchSummaryList.onToggle}>
            {renderDataGridComponent('summary_view')}
          </DropDownPanel>
        </Card>
      </Box>
      {/* Batch List */}
      <Box px={3} mt={1}>
        <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px', mb: 1}}>
          <DropDownPanel
            title={'Batch List'}
            secondaryTitle={
              <FlexContainer>
                <LoadingButton
                  type="submit"
                  disabled={isLoading}
                  loading={isLoading}
                  onClick={onRefreshBatchList}
                  sx={{
                    backgroundColor: theme.palette.primary.light
                  }}
                  variant="outlined">
                  Refresh
                </LoadingButton>
                <LoadingButton
                  type="submit"
                  color="success"
                  loading={reviewLoading}
                  disabled={!selectedBatchIds.length}
                  onClick={onReviewBatchList}
                  sx={{
                    marginLeft: '12px',
                    backgroundColor: theme.palette.primary.dark
                  }}
                  loadingPosition="start"
                  variant="contained">
                  Reviewed & Completed
                </LoadingButton>
              </FlexContainer>
            }
            collapse={batchList.value}
            onCollapse={batchList.onToggle}>
            {renderDataGridComponent('batch_view')}
          </DropDownPanel>
        </Card>
      </Box>

      {/* New Batch Dialog */}
      {isNewBatchDialog && (
        <Dialog
          fullWidth
          maxWidth="md"
          open={isNewBatchDialog}
          onClose={handleClose}
          transitionDuration={{
            enter: theme.transitions.duration.shortest,
            exit: 0
          }}
          PaperProps={{
            sx: {
              overflow: 'unset'
            }
          }}
          sx={{
            [`& .${dialogClasses.container}`]: {
              alignItems: 'center',
              justifyContent: 'center'
            },
            '& .MuiStack-root': {
              borderRadius: '8px'
            }
          }}>
          {renderFormContainer()}
        </Dialog>
      )}

      {/* Confirmation Dialog for Posting */}
      {isConfirmDialogOpen && (
        <Dialog
          open={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
          transitionDuration={{
            enter: theme.transitions.duration.shortest,
            exit: 0
          }}
          PaperProps={{
            sx: {
              overflow: 'unset'
            }
          }}
          sx={{
            [`& .${dialogClasses.container}`]: {
              alignItems: 'center',
              justifyContent: 'center'
            },
            '& .MuiStack-root': {
              borderRadius: '8px'
            }
          }}>
          <DialogTitle
            height={0}
            sx={{
              textAlign: 'left',
              borderBottom: `solid 1px ${theme.palette.divider}`
            }}>
            {t('PRIVATE.PATIIENT_ACCOUNTING.CONFIRM_POST.TITLE', {
              ns: 'private'
            })}
          </DialogTitle>
          <DialogContent>
            <Box p={1}>
              {postingInProgress ? (
                <Stack spacing={2} alignItems="center">
                  <CircularProgress size={24} />
                  <Typography variant="body2">
                    Posting in progress, please wait...
                  </Typography>
                </Stack>
              ) : (
                <Typography>
                  {t('PRIVATE.PATIIENT_ACCOUNTING.CONFIRM_POST.DESCRIPTION', {
                    ns: 'private'
                  })}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Stack alignItems="flex-end">
              <Grid item xs={12} display="flex" sm={6} md={12}>
                {renderYesButton()}
                {renderNoButton()}
              </Grid>
            </Stack>
          </DialogActions>
        </Dialog>
      )}

      {/* Remarks Dialog */}
      {isRemarksDialogOpen && (
        <Dialog
          fullWidth
          maxWidth="sm"
          open={isRemarksDialogOpen}
          onClose={handleCloseDialog}
          transitionDuration={{
            enter: theme.transitions.duration.shortest,
            exit: 0
          }}
          PaperProps={{
            sx: {
              overflow: 'unset'
            }
          }}
          sx={{
            [`& .${dialogClasses.container}`]: {
              alignItems: 'center',
              justifyContent: 'center'
            },
            '& .MuiStack-root': {
              borderRadius: '8px'
            }
          }}>
          {renderRemarksTimeline()}
        </Dialog>
      )}

      {openRemoveBatchDialog && (
        <ActionModalDialog
          open={openRemoveBatchDialog}
          onClose={onCloseRemoveBatchDialog}
          title="Do you want to delete this Batch?"
          description={getRemoveBatchName()}
          handleAgreeBtnClick={onRemoveBatch}
          handleCloseBtnClick={onCloseRemoveBatchDialog}
          loader={removeBatchLoading}
          yesLabelText={'Yes'}
          noLabelText={'No'}
          yesButtonColor={APP.PALETTE.ROLES.RED}
          noButtonColor={APP.PALETTE.ROLES.GRAY}
        />
      )}
    </Stack>
  );
};

export default PaymentManualPost;
